import { useDigilaAnalyticsService } from '@digila-ui/analytics/analytics'
import { DigilaContext, EditableStep, MinorUser, StepType, User, WizardActions, WizardData } from '@digila-ui/types'
import { DigiLaParam } from '@shared/digila'
import { ConfigResponse, DigiLaSource, DigiliFlow, LocalizedString } from '@shared/types'
import { ShowModalOptions } from '@ui-common/components/Modal'
import { useGlobalServices, usePersistingActor } from '@ui-common/hooks'
import { KycFinishedHandler, KycFinishedHandlerInput } from '@ui-common/kyc'
import { AuthenticatedEvent } from '@ui-common/state'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep'
import { commonExit, getLanguage } from '@ui-common/utils'
import { useSelector } from '@xstate/react'
import { useCallback } from 'react'

import { FALLBACK_EXIT_URL_SMOBILE, FALLBACK_EXIT_URL_WEB } from '../config'
import {
  ApplicationRejectedEventType,
  createWizardMachine,
  DigiliRequiredEventType,
  GetMinorEvent,
  GuardianKycDoneEventType,
  MinorKycDoneEventType,
  ResetEventType,
  SERVICE_WIZARD_STATE,
  WizardDoneEventType,
  WizardEvent,
  WizardStateContext,
} from '../state/wizardMachine'
import { convertApplicationToWizardData, getDigilaSourceChannel } from '../utils/helpers'

export interface WizardLogic extends WizardActions<StepType | typeof SERVICE_WIZARD_STATE>, DigilaContext {
  authToken?: string
  setToken: (token: string) => void
  setUser: (user: User) => void
  setMinorUser: (user: MinorUser) => void
  onInitialized: (data?: ConfigResponse<DigiLaParam>) => void
  onGuardianKycDone: KycFinishedHandler
  onDigiliRequired: (data: DigiliFlow) => void
  openModal: (options: ShowModalOptions) => void
  isLoading: boolean
  step: StepType | typeof SERVICE_WIZARD_STATE
  params?: Record<DigiLaParam, string>
}

export const useWizardLogic = (): WizardLogic => {
  const { sendPageEditActionEvent, sendPageCustomEvent } = useDigilaAnalyticsService()

  const shouldHydrate = () => true
  const actor = usePersistingActor<ReturnType<typeof createWizardMachine>>(createWizardMachine(), shouldHydrate)

  const { openModal } = useGlobalServices()

  const send = useCallback((event: WizardEvent) => actor.send(event), [actor])

  const {
    step,
    wizardData,
    isEditing,
    config,
    authToken,
    minor: minorUser,
    user,
    availableServices,
    availableAuthorizations,
    result,
  }: Partial<WizardStateContext> = useSelector(actor, ({ value, context }) => ({
    step: value,
    ...context,
  }))

  const exit = async (isFailure: boolean = false) => {
    if (step !== SERVICE_WIZARD_STATE) {
      sendPageCustomEvent(step, 'AbortButton')
    }
    const sourceChannel = getDigilaSourceChannel()
    const fallbackExitUrl: LocalizedString =
      sourceChannel === DigiLaSource.SMobile ? FALLBACK_EXIT_URL_SMOBILE : FALLBACK_EXIT_URL_WEB
    const url = ((isFailure ? config.exitUrls?.failureUrl : config.exitUrls?.successUrl) ?? fallbackExitUrl)[
      getLanguage()
    ]
    await commonExit(url)
  }

  return {
    onReset: useCallback(() => {
      send({ type: ResetEventType })
    }, [send]),
    onInitialized: (cfg?: ConfigResponse<DigiLaParam>) => send({ type: 'INITIALIZED', data: cfg }),
    openModal: useCallback((options: ShowModalOptions) => openModal(options), [openModal]),
    setToken: useCallback(
      (token: string) => send({ type: 'AUTHENTICATION_DONE', data: { authToken: token } } satisfies AuthenticatedEvent),
      [send],
    ),
    setUser: useCallback(
      (fetchedUser: User) => {
        send({ type: 'GET_DATA_DONE', data: fetchedUser } satisfies GetDataDoneEvent<User>)
      },
      [send],
    ),
    setMinorUser: useCallback(
      (fetchedUser: MinorUser) => {
        const applicationData = convertApplicationToWizardData(fetchedUser.pendingApplication)
        send({ type: 'GET_MINOR_DATA_DONE', data: { applicationData, minor: fetchedUser } } satisfies GetMinorEvent)
      },
      [send],
    ),
    isLoading: false,
    step,
    goBack: useCallback(() => {
      send({ type: 'PREVIOUS_STEP' })
    }, [send]),
    proceed: useCallback(
      (data: WizardData) => {
        send({ type: 'NEXT_STEP', data })
      },
      [send],
    ),
    exit: () => {
      void exit()
    },
    updateWizardData: useCallback(
      (data: WizardData) => {
        send({ type: 'SUBMIT', data })
      },
      [send],
    ),
    onGuardianKycDone: useCallback(
      (data: KycFinishedHandlerInput) => {
        send({ type: GuardianKycDoneEventType, data })
      },
      [send],
    ),
    isEditing,
    editData: useCallback(
      (dataType: EditableStep) => {
        sendPageEditActionEvent(dataType)
        send({ type: 'EDIT', step: dataType })
      },
      [send, sendPageEditActionEvent],
    ),
    wizardData: wizardData ?? {},
    authToken,
    minorUser,
    user,
    availableServices,
    availableAuthorizations,
    result,
    params: config?.params,
    onWizardDone: useCallback(
      (data: WizardData) => {
        send({ type: WizardDoneEventType, data })
      },
      [send],
    ),
    onApplicationRejected: useCallback(() => {
      send({ type: ApplicationRejectedEventType })
    }, [send]),
    onMinorKycDone: useCallback(
      (data: KycFinishedHandlerInput) => {
        send({ type: MinorKycDoneEventType, data })
      },
      [send],
    ),
    onDigiliRequired: useCallback(
      (data: DigiliFlow) => {
        send({ type: DigiliRequiredEventType, data })
      },
      [send],
    ),
  }
}
